import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { IconNode, IconSet, NB_ICONS } from './icon.types';

/**
 * `<nb-icon>` — renders a Lucide icon (the exact 0.460.0 set the mockups use).
 *
 * Two ways to supply the icon, both tree-shakeable:
 *  - `[icon]` — pass a Lucide node directly: `import { Bell } from 'lucide'`.
 *  - `name`  — string lookup against icons registered with `provideNbIcons()`.
 *
 * Sizing flows through the inheritable custom properties `--nb-icon-size` /
 * `--nb-icon-stroke`, so a parent component can size all the icons inside it
 * from one scoped rule (mirrors the mockups' `.btn svg{width:19px}` pattern).
 * Set the `size` / `strokeWidth` inputs to override for a single icon.
 *
 * Decorative by default (`aria-hidden`). Pass `label` to expose it to AT as an
 * `img` with an accessible name.
 */
@Component({
  selector: 'nb-icon',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.--nb-icon-size.px]': 'size()',
    '[style.--nb-icon-stroke]': 'strokeWidth()',
  },
  template: `<svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-linecap="round"
    stroke-linejoin="round"
    [attr.aria-hidden]="label() ? null : 'true'"
    [attr.role]="label() ? 'img' : null"
    [attr.aria-label]="label() || null"
    [innerHTML]="markup()"
  ></svg>`,
  styles: [
    `
      :host {
        display: inline-flex;
        line-height: 0;
      }
      svg {
        display: block;
        width: var(--nb-icon-size, 24px);
        height: var(--nb-icon-size, 24px);
        stroke-width: var(--nb-icon-stroke, 2);
      }
    `,
  ],
})
export class NbIcon {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly sets = inject(NB_ICONS, { optional: true }) ?? [];
  private readonly registry: IconSet = Object.assign({}, ...this.sets);

  /** A Lucide icon node (e.g. the `Bell` named export). Takes precedence over `name`. */
  readonly icon = input<IconNode>();
  /** Kebab-case icon name registered via `provideNbIcons` (matches the mockups' `data-lucide`). */
  readonly name = input<string>();
  /** Explicit pixel size; overrides the inherited `--nb-icon-size` (default 24). */
  readonly size = input<number>();
  /** Explicit stroke width; overrides the inherited `--nb-icon-stroke` (default 2). */
  readonly strokeWidth = input<number>();
  /** Accessible name. When omitted the icon is `aria-hidden` (decorative). */
  readonly label = input<string>();

  protected readonly markup = computed<SafeHtml>(() => {
    const node = this.icon() ?? (this.name() ? this.registry[this.name()!] : undefined);
    if (!node) return '';
    return this.sanitizer.bypassSecurityTrustHtml(serializeChildren(node));
  });
}

/** Serialize a Lucide node's children (`[["path",{d}], …]`) to an SVG markup string. */
function serializeChildren(node: IconNode): string {
  const children = node[2] ?? [];
  return children
    .map(([tag, attrs]) => {
      const a = Object.entries(attrs)
        .map(([k, v]) => `${k}="${v}"`)
        .join(' ');
      return `<${tag} ${a}></${tag}>`;
    })
    .join('');
}
